import { AiPowerIcon } from "@/components/icons";
import { isFetchBaseQueryError } from "@/core/auth/utils/authErrors";
import { formatDate } from "@/core/i18n";
import { getRandomHex } from "@/lib/color";
import { cn } from "@/lib/utils";
import { useGetProjectLabelUsageQuery } from "@/modules/project/services/projectApi";
import { Button } from "@/modules/shadcn/ui/button";
import { Input } from "@/modules/shadcn/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/modules/shadcn/ui/popover";
import { Textarea } from "@/modules/shadcn/ui/textarea";
import {
  GenerateDescriptionButton,
  type GenerateLabelDescription,
} from "../GenerateDescriptionButton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/modules/shadcn/ui/tooltip";
import {
  ApiErrorCode,
  LABEL_DESCRIPTION_MAX,
  Label,
  labelSchema,
} from "@repo/schema";
import { Archive, ArchiveRestore, Plus } from "lucide-react";
import { RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiErrorMessage, isApiError } from "@/core/api";
import type { TFunction } from "i18next";

/** What the editor hands back; `description` null = not described. */
export interface LabelEditorInput {
  name: string;
  color: string;
  description?: string | null;
  /** Answer to a name held by an archived label: rename that one first. */
  renameArchived?: { labelId: number; name: string };
}

export interface LabelEditorPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Element the panel sits above (the whole toolbar, not the `+` inside its
   *  padding); defaults to the trigger. */
  anchor?: RefObject<HTMLElement | null>;
  /** null = create mode; a label = edit mode with its fields prefilled. */
  editing: Label | null;
  /** Currently loaded labels, used for the client-side duplicate-name check. */
  existingLabels: Label[];
  /** Needed for the usage lookup behind the archive confirmation; undefined while loading. */
  projectId: number | undefined;
  /** The open image has unsaved edits: archiving waits until they are saved. */
  hasUnsavedChanges: boolean;
  /** Each resolves on success (popover closes); throws to keep it open with
   *  an inline error and the typed values preserved. */
  onCreate: (input: LabelEditorInput) => Promise<void>;
  onUpdate: (labelId: number, input: LabelEditorInput) => Promise<void>;
  onArchive: (labelId: number) => Promise<void>;
  /** Restores an archived label (the name-conflict answer in create mode). */
  onRestore: (labelId: number) => Promise<void>;
  /** Absent when Robopipe Vision is unavailable: the ✨ is not shown at all. */
  onGenerateDescription?: GenerateLabelDescription;
}

const extractErrorMessage = (
  t: TFunction<["label", "common"]>,
  error: unknown,
  fallback: string,
): string => {
  if (isApiError(error)) return apiErrorMessage(error, fallback);
  if (isFetchBaseQueryError(error) && error.status === 409) {
    return t("label:labelEditor.duplicateName");
  }
  return fallback;
};

/** The archived label a `PROJECT_LABEL_NAME_ARCHIVED` conflict names, if that's what `error` is. */
const archivedLabelOf = (error: unknown): Label | null => {
  if (!isApiError(error)) return null;
  if (error.data.code !== ApiErrorCode.PROJECT_LABEL_NAME_ARCHIVED) return null;
  const parsed = labelSchema.safeParse(error.data.params?.archivedLabel);
  return parsed.success ? parsed.data : null;
};

/**
 * The `+` button of the class toolbar and the panel it opens. The same panel
 * edits an existing label (pencil on a chip): name, colour and the description
 * Robopipe Vision reads, plus Archive, which first lists what it hides.
 */
export const LabelEditorPopover = ({
  open,
  onOpenChange,
  anchor,
  editing,
  ...formProps
}: LabelEditorPopoverProps) => {
  const { t } = useTranslation(["label", "common"]);
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger
        aria-label={t("labelEditor.createAria")}
        title={t("labelEditor.addTitle")}
        className="flex size-[34px] shrink-0 cursor-pointer items-center justify-center rounded-lg text-foreground/60 transition-colors outline-none hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-ring/50 data-popup-open:bg-black/5"
      >
        <Plus className="size-5" />
      </PopoverTrigger>
      <PopoverContent
        anchor={anchor}
        align="start"
        side="top"
        sideOffset={8}
        className="w-[296px] gap-3 rounded-2xl p-5"
      >
        {/* Keyed so every open (and every switch between labels) starts from a
            fresh form: state initialisers read the label at mount time and
            nothing flips while the close animation is still on screen. */}
        <LabelEditorForm
          key={editing?.id ?? "new"}
          editing={editing}
          onClose={() => onOpenChange(false)}
          {...formProps}
        />
      </PopoverContent>
    </Popover>
  );
};

type LabelEditorFormProps = Omit<
  LabelEditorPopoverProps,
  "open" | "onOpenChange" | "anchor"
> & { onClose: () => void };

const LabelEditorForm = ({
  editing,
  existingLabels,
  projectId,
  hasUnsavedChanges,
  onCreate,
  onUpdate,
  onArchive,
  onRestore,
  onGenerateDescription,
  onClose,
}: LabelEditorFormProps) => {
  const { t } = useTranslation(["label", "common"]);
  const isEdit = editing !== null;
  const nameRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(editing?.name ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [color, setColor] = useState(() => editing?.color ?? getRandomHex());
  // Separate from `color` so a partially-typed hex (e.g. "#a") doesn't get
  // submitted — only a full valid hex commits to `color`, mirroring
  // ColorPicker's own hex-input validation.
  const [colorInput, setColorInput] = useState(color);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Set when the name is held by an archived label: the form gives way to the
  // restore / rename choice, and "back" returns to it with the typed values.
  const [conflict, setConflict] = useState<Label | null>(null);
  const [renameInput, setRenameInput] = useState("");

  // Editing an existing name almost always means retyping it.
  useEffect(() => {
    if (isEdit) nameRef.current?.select();
  }, [isEdit]);

  // Fresh on every open: saving annotations does not invalidate this tag.
  const {
    data: usage,
    isLoading: usageLoading,
    isError: usageFailed,
  } = useGetProjectLabelUsageQuery(
    { projectId: projectId ?? 0, labelId: editing?.id ?? 0 },
    { skip: !isEdit || !projectId, refetchOnMountOrArgChange: true },
  );

  const handleNativeColorChange = (next: string) => {
    setColor(next);
    setColorInput(next);
  };

  const handleHexInputChange = (next: string) => {
    setColorInput(next);
    if (/^#[0-9A-Fa-f]{6}$/.test(next)) {
      setColor(next);
    }
  };

  const submit = async (renameArchived?: { labelId: number; name: string }) => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t("labelEditor.nameRequired"));
      nameRef.current?.focus();
      return;
    }
    const clash = existingLabels.some(
      (l) =>
        l.name.trim().toLowerCase() === trimmed.toLowerCase() &&
        l.id !== editing?.id,
    );
    if (clash) {
      setError(t("labelEditor.duplicateName"));
      return;
    }
    setSubmitting(true);
    setError(null);
    const input: LabelEditorInput = {
      name: trimmed,
      color,
      description: description.trim() || null,
      ...(renameArchived && { renameArchived }),
    };
    try {
      if (editing) await onUpdate(editing.id, input);
      else await onCreate(input);
      onClose();
    } catch (err) {
      const archived = renameArchived ? null : archivedLabelOf(err);
      if (archived) {
        setConflict(archived);
        setRenameInput(
          t("labelEditor.renameArchivedDefault", { name: archived.name }),
        );
        return;
      }
      setError(
        extractErrorMessage(
          t,
          err,
          editing ? t("labelEditor.saveFailed") : t("labelEditor.createFailed"),
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };
  const handleSubmit = () => void submit();

  const handleRestoreArchived = async () => {
    if (!conflict) return;
    setSubmitting(true);
    setError(null);
    try {
      await onRestore(conflict.id);
      onClose();
    } catch (err) {
      setError(extractErrorMessage(t, err, t("labelEditor.restoreFailed")));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRenameArchived = () => {
    if (!conflict) return;
    const next = renameInput.trim();
    if (!next) {
      setError(t("labelEditor.nameRequired"));
      return;
    }
    void submit({ labelId: conflict.id, name: next });
  };

  // Replaces the text but saves nothing: Save is still the labeller's call.
  const handleGenerate = async () => {
    if (!onGenerateDescription) return;
    setGenerating(true);
    setError(null);
    try {
      setDescription(
        await onGenerateDescription({
          labelId: editing?.id,
          name: name.trim(),
          currentDescription: description,
        }),
      );
    } catch (err) {
      setError(extractErrorMessage(t, err, t("settings.descriptions.generateFailed")));
    } finally {
      setGenerating(false);
    }
  };

  // Archiving is never refused for being in use (it is reversible), only
  // while the open image has unsaved edits, which a refetch could clobber.
  const usagePending = isEdit && usageLoading && !usage && !usageFailed;
  const archiveBlocked = hasUnsavedChanges;

  const impactLines: string[] = [];
  if (usage) {
    if (usage.annotations > 0) {
      impactLines.push(
        t("labelEditor.impactRegions", { count: usage.annotations }),
      );
    }
    if (usage.tasksBecomingEmpty > 0) {
      impactLines.push(
        t("labelEditor.impactBecomingEmpty", { count: usage.tasksBecomingEmpty }),
      );
    }
    if (usage.models > 0) {
      impactLines.push(t("labelEditor.impactModels", { count: usage.models }));
    }
    if (usage.evalLimits > 0) {
      impactLines.push(
        t("labelEditor.impactEvalLimits", { count: usage.evalLimits }),
      );
    }
  }

  const performArchive = async () => {
    if (!editing) return;
    setSubmitting(true);
    setError(null);
    try {
      await onArchive(editing.id);
      onClose();
    } catch (err) {
      setConfirmingArchive(false);
      setError(extractErrorMessage(t, err, t("labelEditor.archiveFailed")));
    } finally {
      setSubmitting(false);
    }
  };

  const archiveButton = (
    <Button
      type="button"
      variant="outline"
      onClick={() => setConfirmingArchive(true)}
      disabled={archiveBlocked || usagePending || submitting}
      className={cn(
        "gap-1.5 text-destructive",
        archiveBlocked && "bg-black/5 text-foreground/40 shadow-none",
      )}
    >
      <Archive className="size-4" />
      {t("labelEditor.archive")}
    </Button>
  );

  if (conflict) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium">
            {t("labelEditor.archivedConflictTitle", { name: conflict.name })}
          </p>
          <p className="text-xs text-foreground/70">
            {t("labelEditor.archivedConflictBody", {
              date: conflict.deletedAt
                ? formatDate(conflict.deletedAt, "date")
                : "",
            })}
          </p>
        </div>
        {/* Restoring only answers a create: while renaming an active label it
            would hand the name straight back to a second holder. */}
        {!isEdit && (
          <div className="flex flex-col gap-1.5">
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleRestoreArchived()}
              disabled={submitting || hasUnsavedChanges}
              className="gap-1.5"
            >
              <ArchiveRestore className="size-4" />
              {t("labelEditor.restoreArchived")}
            </Button>
            <p className="text-xs text-muted-foreground">
              {hasUnsavedChanges
                ? t("archivedLabels.saveFirst")
                : t("labelEditor.restoreArchivedHelp")}
            </p>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Input
            value={renameInput}
            placeholder={t("labelEditor.renameArchivedPlaceholder")}
            aria-label={t("labelEditor.renameArchivedPlaceholder")}
            className="h-9 text-sm"
            onChange={(e) => {
              setRenameInput(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleRenameArchived();
              }
            }}
          />
          <Button
            type="button"
            onClick={handleRenameArchived}
            disabled={submitting}
          >
            {t("labelEditor.renameArchivedAndSave")}
          </Button>
          <p className="text-xs text-muted-foreground">
            {t("labelEditor.renameArchivedHelp")}
          </p>
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setConflict(null);
            setError(null);
          }}
          disabled={submitting}
        >
          {t("common:actions.cancel")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Input
        ref={nameRef}
        autoFocus
        placeholder={t("labelEditor.namePlaceholder")}
        value={name}
        className="h-11 text-sm"
        onChange={(e) => {
          setName(e.target.value);
          if (error) setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
          }
        }}
      />
      {/* Same text Robopipe Vision reads and the settings dialog edits.
          Fixed height, no resize handle: the box IS the size hint. */}
      <Textarea
        maxLength={LABEL_DESCRIPTION_MAX}
        placeholder={t("labelEditor.descriptionPlaceholder")}
        value={description}
        readOnly={generating}
        onChange={(e) => setDescription(e.target.value)}
        className="h-20 min-h-0 resize-none field-sizing-fixed border-0 px-3 py-2 text-sm shadow-none"
      />
      <div className="flex items-start gap-2">
        <p className="flex flex-1 items-start gap-2 text-xs text-muted-foreground">
          <AiPowerIcon className="mt-px size-4 shrink-0" aria-hidden />
          <span>{t("labelEditor.descriptionHelp")}</span>
        </p>
        {onGenerateDescription && (
          <GenerateDescriptionButton
            pending={generating}
            disabledReason={name.trim() ? null : t("labelEditor.nameFirst")}
            onClick={() => void handleGenerate()}
          />
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={color}
          onChange={(e) => handleNativeColorChange(e.target.value)}
          aria-label={t("labelEditor.colour")}
          className="h-9 w-11 shrink-0 cursor-pointer rounded-md border-0 p-0 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:rounded-md [&::-moz-color-swatch]:border-none"
        />
        <Input
          value={colorInput}
          onChange={(e) => handleHexInputChange(e.target.value)}
          className="h-9 font-mono text-sm"
          maxLength={7}
          aria-label={t("labelEditor.colourHex")}
        />
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}

      {confirmingArchive && editing ? (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-medium">
            {t("labelEditor.archiveConfirmTitle", { name: editing.name })}
          </p>
          {usageFailed ? (
            <p className="text-xs text-foreground/70">
              {t("labelEditor.usageCheckFailed")}
            </p>
          ) : (
            <ul className="flex list-disc flex-col gap-0.5 pl-4 text-xs text-foreground/70">
              {impactLines.length > 0 ? (
                impactLines.map((line) => <li key={line}>{line}</li>)
              ) : (
                <li>{t("labelEditor.impactNone")}</li>
              )}
            </ul>
          )}
          <p className="text-xs text-foreground/70">
            {t("labelEditor.archiveRestoreHint")}
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="destructive"
              onClick={() => void performArchive()}
              disabled={submitting}
              className="gap-1.5"
            >
              <Archive className="size-4" />
              {t("labelEditor.archive")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmingArchive(false)}
              disabled={submitting}
            >
              {t("common:actions.cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || generating}
          >
            {isEdit ? t("common:actions.save") : t("common:actions.create")}
          </Button>
          {isEdit &&
            (archiveBlocked ? (
              // A disabled button emits no hover events, so the wrapper span
              // carries the tooltip.
              <TooltipProvider delay={200}>
                <Tooltip>
                  <TooltipTrigger render={<span className="inline-flex" />}>
                    {archiveButton}
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[216px]">
                    {t("labelEditor.archiveSaveFirst")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              archiveButton
            ))}
        </div>
      )}
    </div>
  );
};
